/**
 * 
 * Zeigt die Auszahlungsplan-Details für ein Darlehen in einem Dialog.
 * Enthält:
 * - Bereitstellungszins-Eingabe
 * - Tabelle mit Auszahlungspunkten (Jahr/Monat-Dropdowns + Betrag)
 * - Button zum Hinzufügen weiterer Auszahlungen
 * - Speichern/Abbrechen Buttons
 * - Summen-Validierung
 */
import React, { useState, useEffect, useCallback, useMemo, forwardRef, useImperativeHandle } from 'react';
import {
    Box,
    Typography,
    TextField,
    Select,
    MenuItem,
    FormControl,
    IconButton,
    Button as MuiButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    CircularProgress,
    Alert,
} from '@mui/material';
import {
    Delete as DeleteIcon,
    Add as AddIcon,
} from '@mui/icons-material';
import { NumericFormat } from 'react-number-format';
import { useParams } from 'react-router';
import { apiService } from '../../services/api';
import { formatNumber } from '../../utils/formatters';
import { LoanWithPlan, AuszahlungEntry } from '../../types';
import InfoTooltip from '../common/overlays/InfoTooltip';
import { colors } from '../../styles/colors';

/** Handle für externen Zugriff auf Save/Cancel */
export interface LoanAuszahlungDetailHandle {
    save: () => Promise<void>;
    cancel: () => void;
}

interface LoanAuszahlungDetailProps {
    /** Das Darlehen für das der Auszahlungsplan angezeigt wird */
    loan: LoanWithPlan;
    /** Projektlaufzeit in Jahren (für Dropdown-Optionen) */
    projektLaufzeit: number;
    /** Callback wenn sich Auszahlungen geändert haben */
    onAuszahlungChanged?: () => void;
    /** Callback zum Schließen des Dialogs */
    onClose?: () => void;
    /** Ob Bearbeitung erlaubt ist */
    canEdit?: boolean;
    /** Callback bei Änderung des Speicher-Status (für externe Buttons) */
    onSavingChange?: (saving: boolean) => void;
}

/** Alle Monat-Optionen */
const ALL_MONAT_OPTIONS = [
    { value: 1, label: 'Jan' },
    { value: 2, label: 'Feb' },
    { value: 3, label: 'Mär' },
    { value: 4, label: 'Apr' },
    { value: 5, label: 'Mai' },
    { value: 6, label: 'Jun' },
    { value: 7, label: 'Jul' },
    { value: 8, label: 'Aug' },
    { value: 9, label: 'Sep' },
    { value: 10, label: 'Okt' },
    { value: 11, label: 'Nov' },
    { value: 12, label: 'Dez' },
];

/**
 * Berechnet den Start-Zeitpunkt (Jahr und Monat) basierend auf beginn_monate_relativ_betrieb
 */
const berechneStartZeitpunkt = (beginnMonate: number): { jahr: number; monat: number } => {
    if (beginnMonate > 0) {
        // Vor Betriebsbeginn
        const jahr = -Math.ceil(beginnMonate / 12);
        const monat = 12 - ((beginnMonate - 1) % 12);
        return { jahr, monat };
    } else if (beginnMonate < 0) {
        // Nach Betriebsbeginn
        const monateNach = Math.abs(beginnMonate);
        let jahr = 1 + Math.floor(monateNach / 12);
        let monat = 1 + (monateNach % 12);
        if (monat > 12) {
            monat -= 12;
            jahr += 1;
        }
        return { jahr, monat };
    } else {
        // Bei Betriebsbeginn
        return { jahr: 1, monat: 1 };
    }
};

/**
 * Berechnet das Ende-Jahr basierend auf Start und Laufzeit
 * (Vereinfacht: Start-Jahr + Laufzeit)
 */
const berechneEndeJahr = (startJahr: number, laufzeitJahre: number): number => {
    let endeJahr = startJahr + laufzeitJahre;
    // Jahr 0 überspringen
    if (startJahr < 0 && endeJahr >= 0) {
        endeJahr += 1;
    }
    return endeJahr;
};

const LoanAuszahlungDetail = forwardRef<LoanAuszahlungDetailHandle, LoanAuszahlungDetailProps>(({
    loan,
    projektLaufzeit,
    onAuszahlungChanged,
    onClose,
    canEdit = true,
    onSavingChange,
}, ref) => {
    const { projectId } = useParams<{ projectId: string }>();

    const [auszahlungen, setAuszahlungen] = useState<AuszahlungEntry[]>([]);
    const [originalAuszahlungen, setOriginalAuszahlungen] = useState<AuszahlungEntry[]>([]);
    const [bereitstellungszins, setBereitstellungszins] = useState<number>(0);
    const [originalBereitstellungszins, setOriginalBereitstellungszins] = useState<number>(0);
    const [darlehenshoehe, setDarlehenshoehe] = useState<number>(0);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const updateSaving = useCallback((saving: boolean) => {
        setIsSaving(saving);
        onSavingChange?.(saving);
    }, [onSavingChange]);

    // Darlehensparameter aus loan extrahieren
    const beginnMonate = loan.beginn_monate_relativ_betrieb ?? 0;
    const laufzeitJahre = loan.laufzeit_jahre ?? 15;

    // Start- und Ende-Zeitpunkt berechnen
    const { startJahr, startMonat, endeJahr } = useMemo(() => {
        const start = berechneStartZeitpunkt(beginnMonate);
        const ende = berechneEndeJahr(start.jahr, laufzeitJahre);
        return {
            startJahr: start.jahr,
            startMonat: start.monat,
            endeJahr: ende,
        };
    }, [beginnMonate, laufzeitJahre]);

    // Jahr-Optionen basierend auf Darlehenslaufzeit
    const jahrOptions = useMemo(() => {
        const options: { value: number; label: string }[] = [];
        for (let jahr = startJahr; jahr <= endeJahr; jahr++) {
            if (jahr === 0) continue; // Jahr 0 gibt es nicht
            options.push({ value: jahr, label: `Jahr ${jahr}` });
        }
        return options;
    }, [startJahr, endeJahr]);

    // Monat-Optionen für ein bestimmtes Jahr (gefiltert nach Darlehenslaufzeit)
    const getMonatOptionsForYear = useCallback((jahr: number): typeof ALL_MONAT_OPTIONS => {
        let minMonat = 1;
        let maxMonat = 12;

        // Im Start-Jahr: Nur Monate ab Start-Monat
        if (jahr === startJahr) {
            minMonat = startMonat;
        }

        // Im Ende-Jahr: Alle Monate erlaubt (Tilgung kann im ganzen Jahr erfolgen)
        // (Optional: Hier könnte man auch den Ende-Monat berechnen)

        return ALL_MONAT_OPTIONS.filter(m => m.value >= minMonat && m.value <= maxMonat);
    }, [startJahr, startMonat]);

    // Daten laden
    useEffect(() => {
        const loadData = async () => {
            if (!projectId || !loan.id) return;

            setIsLoading(true);
            setError(null);

            try {
                const response = await apiService.getAuszahlungen(projectId, loan.id);
                setAuszahlungen(response.entries);
                setOriginalAuszahlungen(response.entries);
                setBereitstellungszins(response.bereitstellungszins);
                setOriginalBereitstellungszins(response.bereitstellungszins);
                setDarlehenshoehe(response.darlehenshoehe);
            } catch (err) {
                setError('Fehler beim Laden der Auszahlungsdaten');
            } finally {
                setIsLoading(false);
            }
        };

        loadData();
    }, [projectId, loan.id]);

    // Restbetrag für Hauptauszahlung berechnen
    const restbetrag = useMemo(() => {
        const andereSumme = auszahlungen
            .filter(a => !a.ist_hauptauszahlung)
            .reduce((sum, a) => sum + a.betrag, 0);
        return Math.max(0, darlehenshoehe - andereSumme);
    }, [auszahlungen, darlehenshoehe]);

    // Hat Änderungen?
    const hasChanges = useMemo(() => {
        if (bereitstellungszins !== originalBereitstellungszins) return true;
        if (auszahlungen.length !== originalAuszahlungen.length) return true;
        return auszahlungen.some((a, i) => {
            const orig = originalAuszahlungen[i];
            if (!orig) return true;
            return a.betriebsjahr !== orig.betriebsjahr ||
                a.monat !== orig.monat ||
                a.betrag !== orig.betrag;
        });
    }, [auszahlungen, originalAuszahlungen, bereitstellungszins, originalBereitstellungszins]);

    // Sortiert Auszahlungen chronologisch (Jahr aufsteigend, dann Monat aufsteigend)
    const sortAuszahlungen = useCallback((list: AuszahlungEntry[]): AuszahlungEntry[] => {
        return [...list].sort((a, b) => {
            // Jahr vergleichen (Jahr 0 gibt es nicht, also kein Sonderfall nötig)
            if (a.betriebsjahr !== b.betriebsjahr) {
                return a.betriebsjahr - b.betriebsjahr;
            }
            // Bei gleichem Jahr nach Monat sortieren
            return a.monat - b.monat;
        });
    }, []);

    // Prüft ob ein Zeitpunkt bereits existiert (für Duplikat-Validierung)
    const getDuplikate = useMemo((): Set<string> => {
        const seen = new Map<string, number>();
        const duplicates = new Set<string>();

        auszahlungen.forEach((a) => {
            const key = `${a.betriebsjahr}-${a.monat}`;
            const count = (seen.get(key) ?? 0) + 1;
            seen.set(key, count);
            if (count > 1) {
                duplicates.add(key);
            }
        });

        return duplicates;
    }, [auszahlungen]);

    const handleJahrChange = useCallback((index: number, neuesJahr: number) => {
        setAuszahlungen(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], betriebsjahr: neuesJahr };
            return sortAuszahlungen(updated);
        });
    }, [sortAuszahlungen]);

    const handleMonatChange = useCallback((index: number, neuerMonat: number) => {
        setAuszahlungen(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], monat: neuerMonat };
            return sortAuszahlungen(updated);
        });
    }, [sortAuszahlungen]);

    const handleBetragChange = useCallback((index: number, neuerBetrag: number) => {
        setAuszahlungen(prev => {
            const updated = [...prev];
            if (!updated[index].ist_hauptauszahlung) {
                updated[index] = { ...updated[index], betrag: neuerBetrag };
            }
            return updated;
        });
    }, []);

    const handleBereitstellungszinsChange = useCallback((neuerZins: number) => {
        setBereitstellungszins(neuerZins);
    }, []);

    const handleAddAuszahlung = useCallback(() => {
        const letzte = auszahlungen[auszahlungen.length - 1];
        let neuesJahr = letzte?.betriebsjahr ?? startJahr;
        let neuerMonat = (letzte?.monat ?? startMonat - 1) + 3;

        // Monat-Überlauf behandeln
        if (neuerMonat > 12) {
            neuerMonat -= 12;
            neuesJahr += 1;
            if (neuesJahr === 0) neuesJahr = 1; // Jahr 0 überspringen
        }

        // Sicherstellen, dass Jahr innerhalb der Laufzeit liegt
        if (neuesJahr > endeJahr) {
            neuesJahr = endeJahr;
            neuerMonat = 12;
        }

        // Sicherstellen, dass Monat für das Jahr gültig ist
        const gueltigeMonateForYear = getMonatOptionsForYear(neuesJahr);
        if (!gueltigeMonateForYear.some(m => m.value === neuerMonat)) {
            neuerMonat = gueltigeMonateForYear[0]?.value ?? 1;
        }

        const neueAuszahlung: AuszahlungEntry = {
            betriebsjahr: neuesJahr,
            monat: neuerMonat,
            betrag: 0,
            ist_hauptauszahlung: false,
        };

        setAuszahlungen(prev => sortAuszahlungen([...prev, neueAuszahlung]));
    }, [auszahlungen, startJahr, startMonat, endeJahr, getMonatOptionsForYear, sortAuszahlungen]);

    const handleDeleteAuszahlung = useCallback((index: number) => {
        setAuszahlungen(prev => {
            const updated = [...prev];
            if (!updated[index].ist_hauptauszahlung) {
                updated.splice(index, 1);
            }
            return updated;
        });
    }, []);

    const handleSave = useCallback(async () => {
        if (!projectId || !loan.id) return;

        // Duplikat-Validierung
        if (getDuplikate.size > 0) {
            setError('Es gibt doppelte Zeitpunkte. Jede Auszahlung muss einen eindeutigen Zeitpunkt haben.');
            return;
        }

        updateSaving(true);
        setError(null);

        try {
            const auszahlungenZumSpeichern = auszahlungen.map(a => ({
                ...a,
                betrag: a.ist_hauptauszahlung ? restbetrag : a.betrag,
            }));

            await apiService.updateAuszahlungen(projectId, loan.id, {
                entries: auszahlungenZumSpeichern,
                bereitstellungszins,
            });

            setOriginalAuszahlungen(auszahlungenZumSpeichern);
            setOriginalBereitstellungszins(bereitstellungszins);
            onAuszahlungChanged?.();
            onClose?.();
        } catch (err) {
            setError('Fehler beim Speichern');
        } finally {
            updateSaving(false);
        }
    }, [projectId, loan.id, auszahlungen, bereitstellungszins, restbetrag, getDuplikate, updateSaving, onAuszahlungChanged, onClose]);

    const handleCancel = useCallback(() => {
        setAuszahlungen(originalAuszahlungen);
        setBereitstellungszins(originalBereitstellungszins);
        onClose?.();
    }, [originalAuszahlungen, originalBereitstellungszins, onClose]);

    useImperativeHandle(ref, () => ({
        save: handleSave,
        cancel: handleCancel,
    }), [handleSave, handleCancel]);

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={32} />
            </Box>
        );
    }

    return (
        <Box sx={{ pt: 1 }}>
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}
            {/* Bereitstellungszins */}
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                mt: 1,
                mb: 3,
                p: 2,
                backgroundColor: 'grey.50',
                borderRadius: 2,
            }}>
                <InfoTooltip
                    title="Zinssatz, der auf noch nicht abgerufene Darlehensbeträge berechnet wird. Fällt an, wenn das Darlehen in mehreren Tranchen ausgezahlt wird."
                    placement="top"
                >
                    <Typography
                        variant="body2"
                        sx={{
                            fontWeight: "medium",
                            minWidth: 140
                        }}>
                        Bereitstellungszins:
                    </Typography>
                </InfoTooltip>
                <NumericFormat
                    value={bereitstellungszins}
                    onValueChange={(values) => {
                        const num = values.floatValue ?? 0;
                        handleBereitstellungszinsChange(num);
                    }}
                    thousandSeparator="."
                    decimalSeparator=","
                    decimalScale={2}
                    allowNegative={false}
                    customInput={TextField}
                    size="small"
                    disabled={!canEdit}
                    sx={{ width: 100 }}
                    slotProps={{
                        input: {
                            sx: {
                                borderRadius: '8px',
                                backgroundColor: 'white',
                            },
                        },
                    }}
                />
                <Typography variant="body2" sx={{
                    color: "text.secondary"
                }}>
                    % p.a.
                </Typography>
            </Box>
            {/* Auszahlungsplan-Tabelle */}
            <Box sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                <Table size="small" sx={{ tableLayout: 'fixed' }}>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: colors.background.tableHeader }}>
                            <TableCell sx={{ fontWeight: 600, width: '35%', borderBottom: '1px solid', borderColor: 'divider' }}>Jahr</TableCell>
                            <TableCell sx={{ fontWeight: 600, width: '30%', borderBottom: '1px solid', borderColor: 'divider' }}>Monat</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, width: '25%', borderBottom: '1px solid', borderColor: 'divider' }}>Betrag</TableCell>
                            <TableCell sx={{ width: '10%', borderBottom: '1px solid', borderColor: 'divider' }}></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {auszahlungen.map((auszahlung, index) => {
                            const isLastRow = index === auszahlungen.length - 1;
                            const zeitpunktKey = `${auszahlung.betriebsjahr}-${auszahlung.monat}`;
                            const isDuplicate = getDuplikate.has(zeitpunktKey);

                            // Hintergrundfarbe: Duplikat (rot) > Hauptauszahlung (blau) > transparent
                            const bgColor = isDuplicate
                                ? colors.state.hoverError
                                : auszahlung.ist_hauptauszahlung
                                    ? colors.state.hoverPrimary
                                    : 'transparent';
                            const hoverBgColor = isDuplicate
                                ? colors.state.hoverErrorMedium
                                : auszahlung.ist_hauptauszahlung
                                    ? colors.state.hoverPrimaryMedium
                                    : colors.state.hover;

                            return (
                                <TableRow
                                    key={index}
                                    sx={{
                                        backgroundColor: bgColor,
                                        '&:hover': {
                                            backgroundColor: hoverBgColor,
                                        },
                                    }}
                                >
                                    {/* Jahr */}
                                    <TableCell sx={{ py: 1.5, borderBottom: isLastRow ? 'none' : undefined }}>
                                        <FormControl size="small" fullWidth>
                                            <Select
                                                value={auszahlung.betriebsjahr}
                                                onChange={(e) => handleJahrChange(index, e.target.value as number)}
                                                disabled={!canEdit}
                                                sx={{
                                                    borderRadius: '8px',
                                                    backgroundColor: 'white',
                                                    fontSize: '0.875rem',
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: colors.border.light,
                                                    },
                                                }}
                                            >
                                                {jahrOptions.map(opt => (
                                                    <MenuItem key={opt.value} value={opt.value}>
                                                        {opt.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </TableCell>
                                    {/* Monat */}
                                    <TableCell sx={{ py: 1.5, borderBottom: isLastRow ? 'none' : undefined }}>
                                        <FormControl size="small" fullWidth>
                                            <Select
                                                value={auszahlung.monat}
                                                onChange={(e) => handleMonatChange(index, e.target.value as number)}
                                                disabled={!canEdit}
                                                sx={{
                                                    borderRadius: '8px',
                                                    backgroundColor: 'white',
                                                    fontSize: '0.875rem',
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: colors.border.light,
                                                    },
                                                }}
                                            >
                                                {getMonatOptionsForYear(auszahlung.betriebsjahr).map(opt => (
                                                    <MenuItem key={opt.value} value={opt.value}>
                                                        {opt.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </TableCell>
                                    {/* Betrag */}
                                    <TableCell align="right" sx={{ py: 1.5, borderBottom: isLastRow ? 'none' : undefined }}>
                                        {auszahlung.ist_hauptauszahlung ? (
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontWeight: "medium",
                                                    pr: 1
                                                }}>
                                                {formatNumber(restbetrag, 0)} €
                                            </Typography>
                                        ) : (
                                            <NumericFormat
                                                value={auszahlung.betrag}
                                                onValueChange={(values) => {
                                                    const num = values.floatValue ?? 0;
                                                    handleBetragChange(index, num);
                                                }}
                                                thousandSeparator="."
                                                decimalSeparator=","
                                                decimalScale={0}
                                                allowNegative={false}
                                                customInput={TextField}
                                                size="small"
                                                disabled={!canEdit}
                                                fullWidth
                                                slotProps={{
                                                    input: {
                                                        sx: {
                                                            borderRadius: '8px',
                                                            backgroundColor: 'white',
                                                            '& .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: colors.border.light,
                                                            },
                                                        },
                                                        endAdornment: <Typography variant="body2" sx={{ ml: 0.5, color: 'text.secondary' }}>€</Typography>,
                                                    },
                                                }}
                                            />
                                        )}
                                    </TableCell>
                                    {/* Aktionen */}
                                    <TableCell sx={{ py: 1.5, borderBottom: isLastRow ? 'none' : undefined }}>
                                        {canEdit && (
                                            <Tooltip title={auszahlung.ist_hauptauszahlung ? "Hauptauszahlung kann nicht gelöscht werden" : "Löschen"}>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleDeleteAuszahlung(index)}
                                                        disabled={auszahlung.ist_hauptauszahlung}
                                                        sx={{
                                                            color: auszahlung.ist_hauptauszahlung ? 'action.disabled' : colors.tableActions.delete.main,
                                                            '&:hover': {
                                                                backgroundColor: auszahlung.ist_hauptauszahlung ? 'transparent' : colors.tableActions.delete.hover,
                                                                color: colors.tableActions.delete.hoverText,
                                                            },
                                                        }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </Box>
            {/* Hinzufügen-Button */}
            {canEdit && (
                <Box sx={{ mt: 1 }}>
                    <MuiButton
                        startIcon={<AddIcon />}
                        onClick={handleAddAuszahlung}
                        size="small"
                        sx={{ textTransform: 'none' }}
                    >
                        Auszahlung hinzufügen
                    </MuiButton>
                </Box>
            )}
        </Box>
    );
});

LoanAuszahlungDetail.displayName = 'LoanAuszahlungDetail';

export default LoanAuszahlungDetail;
